import BigNumber from 'bignumber.js';
import {
  MARKET_MONEYLINES,
  MARKET_SPREADS,
  MARKET_TOTALS,
  MORE_WAGERS_FIRST_HALF,
  MORE_WAGERS_SECOND_HALF,
  WAGERS_GAME_PROPS,
  WAGERS_PLAYER_PROPS,
} from 'consts/app';
import {
  BetDetailModel,
  FutureOddModel,
  MarketModel,
  OddModel,
  OddsResponse,
  ShareBetDetailModel,
  ShareBetResponseModel,
} from 'models';

import baseball_image from 'assets/images/category/baseball.png';
import basketball_image from 'assets/images/category/basketball.png';
import football_image from 'assets/images/category/football.png';
import hockey_image from 'assets/images/category/hockey.png';
import soccer_image from 'assets/images/category/soccer.png';
import boxing from 'assets/images/category/boxing.png';
import cricket from 'assets/images/category/cricket.png';
import tennis from 'assets/images/category/tennis.png';
import golf from 'assets/images/category/golf.png';
import mma from 'assets/images/category/mma.png';
import esports from 'assets/images/category/esport.png';
import { BetModel } from 'features/home/homeSlice';

// const converter = useAppSelector(selectCurrentConverter);

export const getPossibleWin = (amount: number, decimalPrice: number): string => {
  const amountBigNumber = new BigNumber(amount);
  return amountBigNumber.times(new BigNumber(decimalPrice)).minus(amountBigNumber).toFixed(2);
};

export const getTypeTeam = (
  bet: ShareBetDetailModel,
  teamName: string,
): 'Home' | 'Tie' | 'Away' => {
  if (teamName === bet.homeTeam) return 'Home';
  else if (teamName === bet.awayTeam) return 'Away';
  return 'Tie';
};

const formatPrice = (price: number, fixed: number): number | string => {
  return `${price > 0 ? '+' : ''}${new BigNumber(price).toFixed(fixed)}`;
};

export const getMarketName = (
  market: string,
):
  | 'Spread'
  | 'Moneyline'
  | 'Total point'
  | 'First half spread'
  | 'First half money'
  | 'First half total'
  | 'Second half spread'
  | 'Second half money'
  | 'Second half total'
  | 'Map Handicap'
  | 'Total point'
  | string => {
  if (MORE_WAGERS_FIRST_HALF[0].markets.includes(market)) return 'First half spread';
  if (MORE_WAGERS_FIRST_HALF[1].markets.includes(market)) return 'First half money';
  if (MORE_WAGERS_FIRST_HALF[2].markets.includes(market)) return 'First half total';

  if (MORE_WAGERS_SECOND_HALF[0].markets.includes(market)) return 'Second half spread';
  if (MORE_WAGERS_SECOND_HALF[1].markets.includes(market)) return 'Second half money';
  if (MORE_WAGERS_SECOND_HALF[2].markets.includes(market)) return 'Second half total';

  if (MARKET_SPREADS.includes(market)) return 'Spread';
  if (MARKET_MONEYLINES.includes(market)) return 'Moneyline';
  if (WAGERS_GAME_PROPS.includes(market) && !market.toLowerCase().includes('total'))
    return getMarket(market);
  if (
    WAGERS_GAME_PROPS.includes(market) &&
    market.toLowerCase().includes('total') &&
    market.toLowerCase().includes('odd_even')
  ) {
    return 'Odd_Even';
  }
  return 'Total point';
};

export const getMarketBetValue = (
  market: string,
  converter: string,
  price: number,
  decimalPrice: number,
  point: number,
  m: string,
): string | number => {
  if (
    MARKET_MONEYLINES.includes(market) ||
    MORE_WAGERS_FIRST_HALF[1].markets.includes(market) ||
    MORE_WAGERS_SECOND_HALF[1].markets.includes(market)
  ) {
    return converter === 'decimal' ? formatPrice(decimalPrice, 2) : formatPrice(price, 0);
  } else if (
    MARKET_SPREADS.includes(market) ||
    MORE_WAGERS_FIRST_HALF[0].markets.includes(market) ||
    MORE_WAGERS_SECOND_HALF[0].markets.includes(market)
  )
    return formatPrice(point, 2);
  else if (
    MARKET_TOTALS.includes(market) ||
    MORE_WAGERS_FIRST_HALF[2].markets.includes(market) ||
    WAGERS_PLAYER_PROPS.includes(market) ||
    MORE_WAGERS_SECOND_HALF[2].markets.includes(market) ||
    (WAGERS_GAME_PROPS.includes(market) &&
      market.toLowerCase().includes('total') &&
      !market.toLowerCase().includes('odd_even'))
  ) {
    if (m === 'Over' || m === 'over') return `O ${point}`;
    else return `U ${point}`;
  } else if (WAGERS_GAME_PROPS.includes(market)) {
    return decimalPrice;
  }
  return 0;
};

export const getMarketValue = (market: MarketModel, converter: string): string | number => {
  if (market === undefined) return 0;
  const point = market?.betPoints || 0;
  const selectionLine = market?.selectionLine || '';
  const marketName = market?.market || '';
  if (
    MARKET_MONEYLINES.includes(marketName) ||
    MORE_WAGERS_FIRST_HALF[1].markets.includes(marketName) ||
    WAGERS_GAME_PROPS.includes(marketName)
  ) {
    return converter === 'decimal'
      ? formatPrice(market.decimalPrice, 2)
      : formatPrice(market.price, 0);
  } else if (
    MARKET_SPREADS.includes(marketName) ||
    MORE_WAGERS_FIRST_HALF[0].markets.includes(marketName)
  )
    return formatPrice(point, 2);
  else if (
    MARKET_TOTALS.includes(marketName) ||
    MORE_WAGERS_FIRST_HALF[2].markets.includes(marketName) ||
    WAGERS_PLAYER_PROPS.includes(marketName)
  ) {
    if (selectionLine === 'over') return `O ${point}`;
    else return `U ${point}`;
  }
  return 0;
};

export const getFuturePrice = (future: FutureOddModel, converter: string) => {
  return converter === 'decimal'
    ? formatPrice(future?.decimalPrice, 2)
    : formatPrice(future?.price, 0);
};

export const getPossibleWinCombo = (bet: ShareBetResponseModel) => {
  const decimalPrices = bet.betDetails.map((detail) =>
    detail.type === 'BET_GAME' ? detail.market.decimalPrice : detail.futureOdd?.decimalPrice,
  );

  const result = decimalPrices.reduce((accumulator, currentValue) => accumulator * currentValue, 1);
  const amountBigNumber = new BigNumber(bet.amount);
  return amountBigNumber.times(new BigNumber(result)).minus(amountBigNumber).toFixed(2);
};

export const getPossibleWinComboWithAmount = (bet: ShareBetResponseModel, amount: number) => {
  const decimalPrices = bet.betDetails.map((detail) =>
    detail.type === 'BET_GAME' ? detail.market.decimalPrice : detail.futureOdd?.decimalPrice,
  );

  const result = decimalPrices.reduce((accumulator, currentValue) => accumulator * currentValue, 1);
  const amountBigNumber = new BigNumber(amount);
  return amountBigNumber.times(new BigNumber(result)).minus(amountBigNumber).toFixed(2);
};

const images = [
  '',
  football_image,
  baseball_image,
  basketball_image,
  hockey_image,
  soccer_image,
  tennis,
  golf,
  mma,
  boxing,
  cricket,
  esports,
];

export const isNotInLive = (startDate: string) => {
  const givenDate = new Date(startDate);

  const currentDate = new Date();

  const differenceInMillis: number = currentDate.getTime() - givenDate.getTime();

  const differenceInHours: number = differenceInMillis / (1000 * 60 * 60);
  if (differenceInHours >= 24) {
    return true;
  } else {
    return false;
  }
};

export const getImg = (category: string) => {
  switch (category.toLowerCase()) {
    case 'American Football':
    case 'football':
      return images[1];
    case 'baseball':
      return images[2];
    case 'basketball':
      return images[3];
    case 'Ice Hockey':
    case 'hockey':
      return images[4];
    case 'soccer':
      return images[5];
    case 'mma':
      return images[8];
    case 'tennis':
      return images[6];
    case 'golf':
      return images[7];
    case 'boxing':
      return images[9];
    case 'cricket':
      return images[10];
    case 'esports':
      return images[11];
    default:
      return images[0];
  }
};

export const renderScore = (score: number) => {
  if (score === undefined) {
    return '--';
  }
  return score;
};

export const getScore = (betDetail: BetDetailModel) => {
  if (betDetail.odds.market.includes('1st') && betDetail.awayScore !== null) {
    return `${betDetail.homeScore} : ${betDetail.awayScore} (1st half)`;
  }
  return `${renderScore(betDetail.game.score?.scoreHomeTotal)} : ${renderScore(
    betDetail.game.score?.scoreAwayTotal,
  )} `;
};

export const statusColor: { [key: string]: string } = {
  WIN: 'bet-win',
  LOSE: 'bet-lose',
  PUSH: 'bet-draw',
};

export const getMarket = (market: string): string => {
  return market
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const groupOddsByMarket = (gameProps: OddModel[]): Map<string, OddModel[]> => {
  const oddsMap: Map<string, OddModel[]> = new Map();
  gameProps.forEach((prop) => {
    const key = prop.marketName;
    const currentList = oddsMap.get(key) || [];
    oddsMap.set(key, [...currentList, prop]);
  });
  return oddsMap;
};

export const groupOddsByMarketId = (gameProps: OddModel[]): Map<string, OddModel[]> => {
  const oddsMap: Map<string, OddModel[]> = new Map();
  gameProps.forEach((prop) => {
    const key = prop.market;
    const currentList = oddsMap.get(key) || [];
    oddsMap.set(key, [...currentList, prop]);
  });
  return oddsMap;
};

export const lowerPrice = (americanPrice: number): number => {
  const LOWER_PRICE_PERCENT = 0.05;
  const lower = Math.floor(Math.abs(americanPrice) * LOWER_PRICE_PERCENT);
  const newPrice = americanPrice + lower;

  if (newPrice <= 0) {
    return newPrice > -100 ? -100 : newPrice;
  } else {
    return newPrice <= 100 ? 100 : newPrice;
  }
};

export const convertOddsAmericanToDecimal = (americanPrice: number): number => {
  if (americanPrice >= 0) {
    return +(americanPrice / 100 + 1).toFixed(2);
  } else {
    return +(100 / Math.abs(americanPrice) + 1).toFixed(2);
  }
};

export const groupBetsToOddsResponse = (bets: BetModel[]): OddsResponse[] => {
  const oddsMap: Map<string, OddModel[]> = new Map();
  bets.forEach((prop) => {
    const key = prop.gameId;
    const currentList = oddsMap.get(key) || [];
    oddsMap.set(key, [...currentList, prop.odd]);
  });
  const result: OddsResponse[] = [];

  oddsMap.forEach((gameOdds, gameId) => {
    result.push({ gameId, gameOdds });
  });

  return result;
};
